import { Component } from "react";
import { useLocation } from "react-router-dom";
import { devError } from "@/utils/logger";

export class ErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    devError("Render failed:", error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-slate-50 px-6" data-testid="route-error-boundary">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm">
          <p className="font-heading text-xs tracking-[0.2em] uppercase text-brand-red mb-3">Something went wrong</p>
          <h1 className="font-heading text-2xl text-navy mb-3">This page hit an unexpected error</h1>
          <p className="font-body text-base text-gray-600 mb-6">Your data is safe. Reload the page or head back to the directory — the rest of the site keeps working.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button type="button" onClick={() => window.location.reload()} data-testid="error-boundary-reload-btn"
              className="inline-flex items-center justify-center rounded-full bg-brand-red text-white font-body font-semibold px-6 py-3 hover:bg-red-700 transition-colors">
              Reload page
            </button>
            <a href="/" data-testid="error-boundary-home-link"
              className="inline-flex items-center justify-center rounded-full border border-navy text-navy font-body font-semibold px-6 py-3 hover:bg-navy hover:text-white transition-colors">
              Back to home
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export function RouteErrorBoundary({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}
